import { Button, ButtonToggleGroup, ListActionBar, SearchInput } from '@hai/ui-react';
import { IButtonProps, IButtonToggleOption, LayoutDirection, SearchInputFilter } from '@hai/ui-react/dist/types';
import classNames from 'classnames';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { NmosServiceActiveContext } from 'src/js/pages/streaming/st2110/st2110';
import { generateDataAuto } from 'src/js/util/automation';

interface Props {
  ActionButtons: ReactNode;
  actionRightPrimary?: IButtonProps;
  actionRightSecondary?: IButtonProps;
  searchKeys?: (string | any)[]; // TODO improve typing for string or one key-pair value
  sortDropdown?: ReactNode;
  statusTypes?: Status[];
  items: any;
  initialSearchFilters?: string | string[] | SearchInputFilter;
  onSearch?: (filters?: string | SearchInputFilter) => void;
  onSelectAll?: (selected: boolean | 'indeterminate' | 'none', filteredItems: any[]) => void; // if set, it will let the view handle it completely
  getStateValue: (model: any) => string;
  children: (filteredList: any[]) => ReactNode;
}

export interface Status {
  hideWhenEmpty?: boolean;
  numberText?: string;
  status: string;
  color: string;
}

/* UNUSED ?
interface Filter {
  label: string;
  value?: string;
  status?: string;
  active?: boolean;
}*/

// ActionBar v2 with passing down the filtered list in the children
const ActionBar = (props: Props) => {
  const {
    ActionButtons,
    actionRightPrimary = false,
    actionRightSecondary = false,
    searchKeys,
    sortDropdown,
    statusTypes = [],
    items,
    onSearch,
    onSelectAll,
    getStateValue,
    children,
  } = props;
  let { initialSearchFilters } = props;

  const [selectedFilter, setSelectedFilter] = useState('None');
  const [searchKey] = useState(searchKeys);
  if (typeof initialSearchFilters === 'string') {
    initialSearchFilters = [initialSearchFilters];
  }
  const [searchItems, setSearchItems] = useState<any>(initialSearchFilters);

  // Build out the status filter area
  const dataStatusFilter = useCallback(
    (cleanItems: any[]) => {
      const status: Status[] = statusTypes.filter((i) => !i.hideWhenEmpty).map((i) => ({ ...i, numberText: '00' }));

      cleanItems?.forEach((item: any) => {
        let existingStatus = status.findIndex((s: Status) => s.status === getStateValue(item));
        if (existingStatus === -1) {
          const statusFromType = statusTypes.find((s: Status) => s.status === getStateValue(item));
          if (statusFromType) {
            status.push({ ...statusFromType, numberText: statusFromType.numberText || '00' });
            existingStatus = status.length - 1;
          }
        }

        if (existingStatus !== -1) {
          status[existingStatus].numberText = (Number(status[existingStatus].numberText) + 1)
            .toString()
            .padStart(2, '0');
        }
      });

      return statusTypes.map((item) => status.find((i) => i.status === item.status)).filter(Boolean);
    },
    [getStateValue, statusTypes],
  );

  // filter click
  const onChangeStatusFilter = (filter: IButtonToggleOption) => {
    const newFilter = filter.active && filter.status ? filter.status : 'None';
    setSelectedFilter(newFilter);
  };

  const getFilteredList = () => {
    let updated: any[] = [];
    // --- Filter section (status buttons such as 'ENCODING', 'MUTED', 'STOPPED', etc)
    if (selectedFilter && selectedFilter !== 'None') {
      updated = items.filter((i: any) => getStateValue(i) === selectedFilter);
    } else {
      updated = items;
    }
    // --- Search section
    if (searchKey && searchItems) {
      updated = updated?.filter((u) => {
        let searchText = '';
        searchKey.forEach((key) => {
          if (typeof key === 'object') {
            const keys = Object.keys(key);
            keys.map((subKey) => {
              const secondLevelKey = key[subKey]; // e.g. "resolution" in stats.resolution
              searchText += ' ' + u[subKey][secondLevelKey];
            });
          } else {
            searchText += ' ' + u[key];
          }
        });
        return searchItems.every((i: string) => searchText.toLowerCase().includes(i.toLowerCase()));
      });
    }
    return updated;
  };

  // This is the list that's going to be passed down to children
  const [selectionChanged, setSelectionChanged] = useState(0);
  const filteredItems = useMemo(
    () => getFilteredList(),
    [items, searchKey, searchItems, selectedFilter, getStateValue, selectionChanged],
  );
  // console.log('ActionBar filteredItems', filteredItems);

  const searchAddFilter = (filter: string | SearchInputFilter) => {
    setSearchItems((old: any) => {
      if (old === undefined) {
        old = [];
      }
      onSearch?.([...old, filter]);
      return [...old, filter];
    });
  };

  const searchRemoveFilter = (filter?: string | SearchInputFilter) => {
    let newList = '';
    setSearchItems((old: any) => {
      if (old === undefined) {
        // console.warn('No search filter to remove');
        return undefined;
      }
      newList = old.filter((item: string) => {
        return item !== filter;
      });
      return newList;
    });
    onSearch?.(newList);
  };

  const Search = (
    <SearchInput
      layoutDirection={LayoutDirection.LEFT}
      collapsible={true}
      onAddFilter={searchAddFilter}
      onRemoveFilter={searchRemoveFilter}
      filters={searchItems}
      searchMode="simple"
      className="ml-3"
    />
  );

  const selectedItems = filteredItems?.filter((p: any) => p.selected);
  const isSelectAll = useMemo(
    () =>
      selectedItems?.length === 0 ? false : selectedItems?.length === filteredItems?.length ? true : 'indeterminate',
    [selectedItems],
  );

  // this is a dual purpose function. It will either let the parent handle the select all if onSelectAll was passed, or do it here internally if not.
  const onSelectAllLocal = onSelectAll
    ? () => onSelectAll(isSelectAll, filteredItems)
    : useCallback(() => {
        // this local routine work except to update the action buttons state
        let anyChanges = 0;
        filteredItems.forEach((i: any) => {
          const newSelectedState = isSelectAll === false;
          if (newSelectedState !== i.selected) {
            anyChanges++;
            i.selected = newSelectedState;
          }
        });
        if (anyChanges) {
          setSelectionChanged(selectionChanged + 1);
        }
        // console.log('[ActionBar] onSelectAllLocal filtered items', filteredItems)
      }, [filteredItems, isSelectAll]);

  // FIXME move next line up in StreamingActionBar due to Makito specific implementation
  const nmosServiceActive = React.useContext(NmosServiceActiveContext);

  return (
    <>
      <ListActionBar
        selectAll={isSelectAll}
        onSelectAll={onSelectAllLocal}
        className={classNames('hai-mt-7 mk-action-bar', nmosServiceActive && 'mk-selection-disabled')}
        data-auto={generateDataAuto('list', 'action bar')}
      >
        {statusTypes.length > 0 ? (
          <ListActionBar.Item className="statustoggle" hideOnSelect={true}>
            <ButtonToggleGroup allowAllDeactivated onOptionClicked={onChangeStatusFilter}>
              {dataStatusFilter(items).map((item) => {
                // If no items to display, revert to no filter
                if (selectedFilter === item?.status && item?.numberText === '00') {
                  if (selectedFilter !== 'None') {
                    // put back selection
                    items.forEach((element: any) => {
                      element.toBeRestored = true;
                    });
                  }
                  setSelectedFilter('None');
                }
                return (
                  <ButtonToggleGroup.StatusFilter
                    key={`list-action-bar-status-filter-${item?.status}`}
                    active={selectedFilter === item?.status}
                    numberText={item?.numberText}
                    disabled={item?.numberText === '00'}
                    status={item?.status}
                    color={item?.color}
                  />
                );
              })}
            </ButtonToggleGroup>
          </ListActionBar.Item>
        ) : (
          <></>
        )}
        <ListActionBar.Item as="button-group" hideOnSelect={false}>
          {ActionButtons}
        </ListActionBar.Item>
        {searchKey ? <ListActionBar.Item alignment="right">{Search}</ListActionBar.Item> : <></>}
        {sortDropdown ? (
          <ListActionBar.Item alignment="right" className="ml-1" data-auto={generateDataAuto('sort', 'dropdown')}>
            {sortDropdown}
          </ListActionBar.Item>
        ) : (
          <></>
        )}
        {actionRightSecondary ? (
          <ListActionBar.Item alignment="right">
            <Button variant="secondary" {...actionRightSecondary} />
          </ListActionBar.Item>
        ) : (
          <></>
        )}
        {actionRightPrimary ? (
          <ListActionBar.Item alignment="right">
            <Button variant="primary" {...actionRightPrimary} />
          </ListActionBar.Item>
        ) : (
          <></>
        )}
      </ListActionBar>
      {children(filteredItems)}
    </>
  );
};

export default ActionBar;
